import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function AccountSearchField({ label, value, onChange, error, helperText, disabled, filterAccountTypes }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && accounts.length === 0) {
      fetchAccounts();
    }
  }, [open]);

  const fetchAccounts = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      // Use /gl-accounts (full list, ~150 active accounts) instead of
      // /gl-accounts-summary which capped at top-20 and hid most expenses.
      const response = await axios.get(`${API_URL}/api/accounting/gl-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const allAccounts = response.data.data.accounts || [];

        const formattedAccounts = allAccounts.map(acc => ({
          id: acc.account_id,
          code: acc.account_code,
          name: acc.account_name,
          type: acc.account_type,
          label: `${acc.account_code} - ${acc.account_name}`,
          searchText: `${acc.account_code} ${acc.account_name} ${acc.account_type}`.toLowerCase()
        }));

        setAccounts(formattedAccounts);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching GL accounts:', err);
      setLoading(false);
    }
  };

  const getSelectedAccount = () => {
    if (!value) return null;
    return accounts.find(acc => acc.id === value) || null;
  };

  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.id : null);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={getSelectedAccount()}
      onChange={handleChange}
      options={accounts}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label || ''}
      filterOptions={(options, { inputValue }) => {
        let filtered = options;
        if (filterAccountTypes && filterAccountTypes.length) {
          const allowed = new Set(filterAccountTypes);
          filtered = filtered.filter((o) => allowed.has(o.type));
        }
        if (inputValue) {
          const searchTerm = inputValue.toLowerCase();
          filtered = filtered.filter((o) => o.searchText.includes(searchTerm));
        }
        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'GL Account'}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="600">
                {option.code}
              </Typography>
              <Chip
                label={option.type}
                size="small"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              {option.name}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
}
